/* eslint-disable max-lines */
import gql from 'graphql-tag';

import { CatchFieldsForPostsViewer } from './fragments/CatchFieldsForPostsViewer';
import { CatchFragments } from './fragments/CatchType';
import { CategoryFragments } from './fragments/CategoryType';
import { FishingWaterFragments } from './fragments/FishingWaterType';
import { ImageFragments } from './fragments/ImageType';
import { PostFragments } from './fragments/PostType';
import { ProductFragments } from './fragments/ProductType';
import { ProductUnitFragments } from './fragments/ProductUnitType';
import { SpeciesFragments } from './fragments/SpeciesTypes';
import { UserFragments } from './fragments/UserType';

export const getNewSingleSpecies = gql`
  query GetNewSingleSpecies($id: String!, $recentCatchLimitDays: Int) {
    entry: singleSpecies(externalId: $id) {
      ...SpeciesId
      legacyId: id
      followedByCurrentUser
      latinName
      displayName
      description
      maxLength
      maxWeight
      iucnStatus
      threatToHumans
      waterTypes
      minCommonDepth
      maxCommonDepth
      catches {
        totalCount
      }
      newCatchCount: catches(filters: { lastNDays: $recentCatchLimitDays }) {
        totalCount
      }
      exampleCatchWithImage {
        ...CatchMetadata
        caughtAtGmt
        species {
          ...SpeciesId
          displayName
        }
        post {
          ...PostId
          createdAt
          displayProductUnits {
            totalCount
          }
          catch {
            ...CatchId
            ...CatchFishingWaterName
            ...CatchSpeciesName
          }
          comments {
            totalCount
          }
          ...PostProductUnits
          ...PostVideoFields
          images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
            totalCount
            edges {
              node {
                ...ImageFields
              }
            }
          }
          webPImages: images(
            first: 1
            croppingStrategy: SMART
            height: 400
            width: 400
            format: WEBP
          ) {
            edges {
              node {
                ...ImageFields
              }
            }
          }
          likedByCurrentUser
          likesCount
          text {
            text
          }
          user {
            ...UserAvatar
            ...UserId
            nickname
            isPremium
            createdAt
          }
        }
      }
      catchesWithImages: catches(first: 10, filters: { withPhotos: true, orderByRecent: true }) {
        edges {
          node {
            ...CatchId
            post {
              ...PostId
              catch {
                ...CatchId
                ...CatchFishingWaterName
                ...CatchSpeciesName
                caughtAtGmt
                weight
                length
              }
              createdAt
              displayProductUnits {
                totalCount
              }
              images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                totalCount
                edges {
                  node {
                    ...ImageFields
                  }
                }
              }
              user {
                ...UserId
                nickname
              }
            }
          }
        }
      }
      followers {
        totalCount
      }
      speciesTips(first: 10) {
        edges {
          node {
            externalId
            tip
            userDescription
            userDisplayName
            user {
              ...UserId
              ...UserAvatar
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${CatchFragments.metadata}
  ${CatchFragments.speciesName}
  ${CatchFragments.fishingWaterName}
  ${PostFragments.productUnits}
  ${PostFragments.videoFields}
  ${ImageFragments.imageFields}
  ${PostFragments.id}
  ${SpeciesFragments.id}
  ${UserFragments.avatar}
  ${UserFragments.id}
`;

export const getNewSpeciesTopBaits = gql`
  query GetNewSpeciesTopBaits($speciesId: String, $first: Int!) {
    topProductUnits(first: $first, speciesExternalId: $speciesId) {
      pageInfo {
        hasNextPage
      }
      edges {
        node {
          numberOfCatches
          position
          productUnit {
            ...ProductUnitId
            image(height: 100) {
              ...ImageFields
            }
            attributeValues(first: 1) {
              edges {
                node {
                  value {
                    name
                  }
                }
              }
            }
            product {
              ...ProductId
              ...ProductBrandName
              displayName
            }
          }
        }
      }
    }
  }
  ${ProductFragments.id}
  ${ProductFragments.brandName}
  ${ProductUnitFragments.id}
  ${ImageFragments.imageFields}
`;

export const getNewSpeciesTopWaters = gql`
  query GetNewSpeciesTopWaters(
    $first: Int
    $boundingBox: BoundingBoxInputObject
    $speciesIDs: [String!]
  ) {
    entry: fishingWaters(
      first: $first
      filters: {
        orderByCatchesCount: true
        publicOnly: true
        withCatchesForSpeciesIds: $speciesIDs
      }
      boundingBox: $boundingBox
    ) {
      edges {
        node {
          ...FishingWaterId
          ...FishingWaterLocationFields
          catchesCount
          displayName
          allReviews: reviews {
            totalCount
            totalAverage(attribute: RATING)
          }
          recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
            edges {
              node {
                ...CatchId
                post {
                  ...PostId
                  images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                    totalCount
                    edges {
                      node {
                        ...ImageFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

export const getTopSpeciesNearLocation = gql`
  query GetTopSpeciesNearLocation(
    $boundingBox: BoundingBoxInputObject
    $boundingCircle: BoundingCircleInputObject
    $excludingSpeciesIds: [String!]
    $first: Int!
    $after: String
  ) {
    topSpeciesNearLocation(
      boundingCircle: $boundingCircle
      boundingBox: $boundingBox
      excludingSpeciesExternalIds: $excludingSpeciesIds
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        startCursor
        hasNextPage
        endCursor
      }
      edges {
        catchesCount
        node {
          ...SpeciesId
          externalId
          legacyId: id
          displayName
          exampleCatchWithImage {
            ...CatchId
            post {
              ...PostId
              images(first: 1, croppingStrategy: SMART, height: 280, width: 400) {
                edges {
                  node {
                    id
                    ...ImageFields
                  }
                }
              }
              webPImages: images(
                first: 1
                croppingStrategy: SMART
                height: 280
                width: 400
                format: WEBP
              ) {
                edges {
                  node {
                    id
                    ...ImageFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${ImageFragments.imageFields}
  ${PostFragments.id}
  ${SpeciesFragments.id}
`;

export const getSpeciesTopBaits = gql`
  query GetSpeciesTopBaits($speciesId: String, $first: Int!) {
    topProductUnits(first: $first, speciesExternalId: $speciesId) {
      pageInfo {
        hasNextPage
      }
      edges {
        node {
          numberOfCatches
          position
          productUnit {
            ...ProductUnitId
            image(height: 100) {
              url
            }
            product {
              ...ProductId
              ...ProductBrandName
              displayName
              category {
                ...CategoryId
                title
              }
            }
          }
        }
      }
    }
  }
  ${CategoryFragments.id}
  ${ProductFragments.id}
  ${ProductFragments.brandName}
  ${ProductUnitFragments.id}
`;

export const getSingleSpecies = gql`
  query GetSingleSpecies($id: String!) {
    entry: singleSpecies(externalId: $id) {
      ...SpeciesId
      legacyId: id
      followedByCurrentUser
      latinName
      displayName
      description
      maxLength
      maxWeight
      iucnStatus
      threatToHumans
      waterTypes
      minCommonDepth
      maxCommonDepth
      catches {
        totalCount
      }
      exampleCatchWithImage {
        ...CatchMetadata
        caughtAtGmt
        species {
          ...SpeciesId
          displayName
        }
        post {
          ...PostId
          createdAt
          displayProductUnits {
            totalCount
          }
          catch {
            ...CatchId
            ...CatchFishingWaterName
            ...CatchSpeciesName
          }
          comments {
            totalCount
          }
          ...PostProductUnits
          ...PostVideoFields
          images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
            totalCount
            edges {
              node {
                ...ImageFields
              }
            }
          }
          webPImages: images(
            first: 1
            croppingStrategy: SMART
            height: 400
            width: 400
            format: WEBP
          ) {
            edges {
              node {
                ...ImageFields
              }
            }
          }
          likedByCurrentUser
          likesCount
          text {
            text
          }
          user {
            ...UserAvatar
            ...UserId
            nickname
            isPremium
            createdAt
          }
        }
      }
      catchesWithImages: catches(first: 8, filters: { withPhotos: true, orderByRecent: true }) {
        edges {
          node {
            ...CatchFieldsForPostsViewer
          }
        }
      }
      followers {
        totalCount
      }
      speciesTips(first: 10) {
        edges {
          node {
            externalId
            tip
            userDescription
            userDisplayName
            user {
              ...UserId
              ...UserAvatar
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${CatchFragments.metadata}
  ${CatchFragments.speciesName}
  ${CatchFragments.fishingWaterName}
  ${PostFragments.productUnits}
  ${PostFragments.videoFields}
  ${ImageFragments.imageFields}
  ${PostFragments.id}
  ${SpeciesFragments.id}
  ${UserFragments.avatar}
  ${UserFragments.id}
  ${CatchFieldsForPostsViewer}
`;

export const getSingleSpeciesName = gql`
  query GetSingleSpeciesName($id: Int!) {
    species: singleSpecies(id: $id) {
      ...SpeciesId
      displayName
    }
  }
  ${SpeciesFragments.id}
`;

export const getSpeciesSuggestedWater = gql`
  query GetSpeciesSuggestedWater($id: String!, $latitude: Float!, $longitude: Float!) {
    entry: singleSpecies(externalId: $id) {
      ...SpeciesId
      suggestedWaters(latitude: $latitude, longitude: $longitude, limit: 12) {
        ...FishingWaterId
        ...FishingWaterLocationFields
        displayName
        catches(filters: { speciesIds: [$id] }) {
          totalCount
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
  ${SpeciesFragments.id}
`;

export const getBiteTimePreview = gql`
  query GetBiteTimePreview($id: String!, $latitude: Float!, $longitude: Float!) {
    speciesForecastPreview(latitude: $latitude, longitude: $longitude, speciesExternalId: $id) {
      rating
      readingAt
    }
  }
`;

export const getSpeciesImages = gql`
  query GetSpeciesImages($ids: [String!]) {
    species(externalIds: $ids, first: 300) {
      edges {
        node {
          ...SpeciesId
          imageCopyrightStatus
          imageCopyrightExpiryDate
          image(width: 240, croppingStrategy: NO_CROP) {
            url
          }
          webPImage: image(width: 240, format: WEBP, croppingStrategy: NO_CROP) {
            url
          }
        }
      }
    }
  }
  ${SpeciesFragments.id}
`;

// Get a specific list of species by their IDs
export const getSpeciesFromList = gql`
  query GetSpeciesFromList($externalIds: [String!]) {
    species(externalIds: $externalIds, first: 10) {
      edges {
        node {
          ...SpeciesId
          externalId
          displayName
          catches {
            totalCount
          }
          exampleCatchWithImage {
            ...CatchId
            post {
              ...PostId
              images(first: 1, croppingStrategy: SMART, height: 280, width: 400) {
                edges {
                  node {
                    id
                    ...ImageFields
                  }
                }
              }
              webPImages: images(
                first: 1
                croppingStrategy: SMART
                height: 280
                width: 400
                format: WEBP
              ) {
                edges {
                  node {
                    id
                    ...ImageFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${SpeciesFragments.id}
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
`;

export const getBasicSpeciesInfoFromList = gql`
  query GetBasicSpeciesInfoFromList($externalIds: [String!]) {
    species(externalIds: $externalIds, first: 10) {
      edges {
        node {
          ...SpeciesId
          externalId
          displayName
        }
      }
    }
  }
  ${SpeciesFragments.id}
`;
